/**
 * LOGOI LAB - Derived Metrics
 * V(x), S(x), Ω(x), Q(x) - All derived from Λ
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { 
  ConsciousnessState, 
  ViabilityResult, 
  EntropyResult, 
  OmegaResult,
  TrajectoryResult 
} from '../types';
import { clamp } from '../utils/math';
import { COLORS, V_STAR, S_STAR, LAMBDA_STAR } from '../domain/constants';

// ═══════════════════════════════════════════════════════════════════════════
// VIABILITY: V(x) = g(Λ) + ε_physical
// ═══════════════════════════════════════════════════════════════════════════

export const VIABILITY_WEIGHTS: Record<string, number> = {
  sleep: 0.06, nutrition: 0.04, exercise: 0.05, energy: 0.05,
  peace: 0.05, gratitude: 0.04, love: 0.04, joy: 0.04,
  clarity: 0.04, focus: 0.04, creativity: 0.03, wisdom: 0.04,
  faith: 0.05, meditation: 0.03, service: 0.03, presence: 0.04,
  family: 0.03, friendship: 0.03, community: 0.02, compassion: 0.03,
  meaning: 0.03, mission: 0.03, contribution: 0.02, legacy: 0.02,
  nourishment: 0.04, taste_presence: 0.02, food_harmony: 0.02, gut_resonance: 0.04,
};

/**
 * Compute Viability V(x)
 * Measures functional capacity of the system to operate in the world.
 */
export function computeViability(
  s: ConsciousnessState,
  Lambda: number,
  weightOverrides?: Record<string, number>
): ViabilityResult {
  const state = s as any;
  const weights = weightOverrides
    ? { ...VIABILITY_WEIGHTS, ...weightOverrides }
    : VIABILITY_WEIGHTS;
  const physicalComponent = Object.entries(weights).reduce(
    (sum, [k, wt]) => sum + (state[k] ?? 0.5) * wt, 0
  );
  
  // V = physical_base + Λ_boost (Logos alignment increases viability)
  const raw = clamp(physicalComponent / 0.55 * 0.55 + 0.15);
  const lambdaBoost = Lambda * 0.35;
  const value = clamp(raw * (0.75 + lambdaBoost));
  
  const breakdown = Object.entries(weights).map(([k, wt]) => ({
    key: k,
    label: k,
    weight: wt,
    value: state[k] ?? 0.5,
    contribution: (state[k] ?? 0.5) * wt
  })).sort((a, b) => b.contribution - a.contribution);
  
  return { value, raw, lambdaBoost, breakdown };
}

// ═══════════════════════════════════════════════════════════════════════════
// ENTROPY: S(x) = (1-Λ)·k + ε
// ═══════════════════════════════════════════════════════════════════════════

export const ENTROPY_FACTORS = [
  { key: "peace", weight: 0.12, label: "Falta de Paz" },
  { key: "clarity", weight: 0.10, label: "Falta de Claridad" },
  { key: "sleep", weight: 0.10, label: "Falta de Sueño" },
  { key: "focus", weight: 0.08, label: "Falta de Enfoque" },
  { key: "energy", weight: 0.08, label: "Falta de Energía" },
  { key: "presence", weight: 0.08, label: "Falta de Presencia" },
  { key: "gut_resonance", weight: 0.07, label: "Falta de Resonancia Intestinal" },
  { key: "wisdom", weight: 0.07, label: "Falta de Sabiduría" },
  { key: "faith", weight: 0.07, label: "Falta de Fe" },
  { key: "nourishment", weight: 0.06, label: "Falta de Nutrición Consciente" },
  { key: "meaning", weight: 0.07, label: "Falta de Sentido" },
  { key: "gratitude", weight: 0.10, label: "Falta de Gratitud" },
];

/**
 * Compute Entropy S(x)
 * Measures disorder and internal noise of the system.
 */
export function computeEntropy(
  s: ConsciousnessState,
  Lambda: number,
  weightOverrides?: Record<string, number>
): EntropyResult {
  const state = s as any;
  
  const breakdown = ENTROPY_FACTORS.map(f => {
    const w = weightOverrides?.[f.key] ?? f.weight;
    const contrib = (1 - (state[f.key] ?? 0.5)) * w;
    return { ...f, weight: w, value: state[f.key] ?? 0.5, contribution: contrib };
  });
  
  const raw = breakdown.reduce((a, b) => a + b.contribution, 0);
  // Λ reduces entropy: alignment with Logos creates order from chaos
  const lambdaEffect = Lambda * 0.4;
  const value = clamp(raw * (1.2 - lambdaEffect));
  
  return { value, raw, lambdaEffect, breakdown };
}

// ═══════════════════════════════════════════════════════════════════════════
// OMEGA: Ω(x) = Λ(x) — Consciousness IS Logos Alignment
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Compute Omega Ω(x) - The Consciousness Index
 * Ω = Λ as the base, with derived metrics as modulating feedback.
 */
export function computeOmega(
  Lambda: number, 
  V: number, 
  S: number, 
  C: number, 
  F: number
): OmegaResult {
  // When derived metrics are healthy, Ω ≈ Λ
  // When derived metrics are unhealthy, Ω < Λ
  const derivedHealth = clamp(V * 0.3 + C * 0.25 - S * 0.25 - Math.min(F, 1) * 0.2 + 0.2);
  const value = clamp(Lambda * 0.6 + derivedHealth * 0.4);
  
  return {
    value,
    lambdaContribution: Lambda * 0.6,
    derivedContribution: derivedHealth * 0.4,
    derivedHealth,
    breakdown: [
      { label: "Λ Logos (60%)", value: Lambda, contribution: Lambda * 0.6, color: COLORS.logos },
      { label: "V Viabilidad (+)", value: V, contribution: V * 0.3 * 0.4, color: COLORS.viability },
      { label: "C Coherencia (+)", value: C, contribution: C * 0.25 * 0.4, color: COLORS.coherence },
      { label: "S Entropía (−)", value: S, contribution: -S * 0.25 * 0.4, color: COLORS.entropy },
      { label: "F Energía Libre (−)", value: F, contribution: -Math.min(F, 1) * 0.2 * 0.4, color: COLORS.freeEnergy },
    ]
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// TRAJECTORY QUALITY: Q(x) = dΛ/dt
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Compute Q(x) - Trajectory Quality
 * Measures speed of convergence to the positive attractor A⁺.
 */
export function computeQ(Lambda: number, V: number, S: number): TrajectoryResult {
  const dV = Math.max(0, V_STAR - V);
  const dS = Math.max(0, S - S_STAR);
  const dL = Math.max(0, LAMBDA_STAR - Lambda);
  const D = Math.sqrt(dV * dV + dS * dS + dL * dL);
  const value = clamp(Lambda * 0.4 + V * 0.2 - S * 0.2 - D * 0.3);
  
  return { 
    value, 
    distanceToAttractor: D, 
    breakdown: { dV, dS, dL, D } 
  };
}
